import type { SliderPreviewProps } from '@videojs/core';
import { applyStateDataAttrs, getSliderPreviewStyle } from '@videojs/core/dom';
import type { PropertyDeclarationMap, PropertyValues } from '@videojs/element';
import { ContextConsumer } from '@videojs/element/context';
import { applyStyles, observeResize } from '@videojs/utils/dom';

import { UIElement } from '../ui-element';
import { sliderContext } from './context';

export class SliderPreviewElement extends UIElement {
  static readonly tagName = 'media-slider-preview';

  static override properties = {
    overflow: { type: String },
  } satisfies PropertyDeclarationMap<'overflow'>;

  overflow: NonNullable<SliderPreviewProps['overflow']> = 'clamp';

  readonly #ctx = new ContextConsumer(this, {
    context: sliderContext,
    subscribe: true,
  });

  #stopObservingResize: (() => void) | null = null;
  #width = 0;

  override connectedCallback(): void {
    super.connectedCallback();

    this.#stopObservingResize = observeResize(this, ([entry]) => {
      this.#width = entry!.contentRect.width;
      this.#applyPosition();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#stopObservingResize?.();
    this.#stopObservingResize = null;
  }

  #applyPosition(): void {
    applyStyles(this, getSliderPreviewStyle(this.#width, this.overflow));
  }

  protected override update(_changed: PropertyValues): void {
    super.update(_changed);

    const ctx = this.#ctx.value;

    if (ctx) applyStateDataAttrs(this, ctx.state, ctx.stateAttrMap);

    this.#applyPosition();
  }
}
